import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FileText, Receipt, Calculator, MessageSquare, BarChart3, DollarSign, Settings as Cog, LogOut, HardHat, Menu, Bell } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import NotificationBell from "@/components/NotificationBell";
import FeedbackButtons from "@/components/FeedbackButtons";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "nav-dashboard" },
  { to: "/dashboard/customers", label: "Customers", icon: Users, tid: "nav-customers" },
  { to: "/dashboard/quotes", label: "Quotes", icon: FileText, tid: "nav-quotes" },
  { to: "/dashboard/invoices", label: "Invoices", icon: Receipt, tid: "nav-invoices" },
  { to: "/dashboard/calculator", label: "Calculator", icon: Calculator, tid: "nav-calculator" },
  { to: "/dashboard/communications", label: "Communications", icon: MessageSquare, tid: "nav-communications" },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell, tid: "nav-notifications" },
  { to: "/dashboard/revenue", label: "Revenue", icon: DollarSign, tid: "nav-revenue" },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3, tid: "nav-reports" },
  { to: "/dashboard/settings", label: "Settings", icon: Cog, tid: "nav-settings" },
];

const SidebarInner = ({ theme, user, onLogout, onNavigate }) => (
  <div className="flex flex-col h-full brand-sidebar" data-testid="sidebar">
    <div className="px-5 pt-5 pb-6 flex items-center gap-2">
      {theme.logo_url ? (
        <img src={theme.logo_url} alt="logo" className="w-10 h-10 rounded-md object-cover border border-slate-200" />
      ) : (
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--qq-header)" }}>
          <HardHat className="w-5 h-5" style={{ color: "var(--qq-header-text)" }} strokeWidth={2.4} />
        </div>
      )}
      <div>
        <div className="font-display font-extrabold text-lg leading-tight">{theme.company_name || "QuickQuote"}</div>
        <div className="text-[10px] uppercase tracking-widest nav-muted font-semibold">Quick Quotes</div>
      </div>
    </div>
    <nav className="flex-1 px-3 space-y-0.5">
      {nav.map(({ to, label, icon: Icon, end, tid }) => (
        <NavLink
          key={to} to={to} end={end} data-testid={tid}
          onClick={onNavigate}
          className={({ isActive }) => `nav-item flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "active" : ""}`}
        >
          <Icon className="w-4 h-4" strokeWidth={2.2} />
          {label}
        </NavLink>
      ))}
    </nav>
    <div className="p-3 nav-divider border-t">
      <div className="px-3 py-2">
        <div className="text-xs font-semibold truncate">{user?.name || user?.email}</div>
        <div className="text-[11px] nav-muted truncate">{user?.email}</div>
      </div>
        <button data-testid="logout-btn" onClick={onLogout} className="nav-item w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, reload } = useTheme();
  const nav_ = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { reload && reload(); /* refresh once user is loaded */ }, [user?.id]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-slate-200">
        <SidebarInner theme={theme} user={user} onLogout={async () => { await logout(); window.location.href = "/"; }} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 border-r border-slate-200">
            <SidebarInner theme={theme} user={user} onLogout={async () => { await logout(); window.location.href = "/"; }} onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 brand-banner px-4 py-3 flex items-center justify-between">
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="p-2 -m-2 brand-banner-text">
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-display font-extrabold text-lg tracking-wide brand-banner-text uppercase" data-testid="top-brand-mobile">
            Quick Quotes
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              data-testid="top-logout-btn-mobile"
              onClick={async () => { await logout(); window.location.href = "/"; }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 brand-banner-text font-bold text-xs border border-white/20"
            >
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </header>
        <header className="hidden lg:flex sticky top-0 z-30 brand-banner px-8 py-3 items-center gap-3" data-testid="top-banner">
          {theme.logo_url ? (
            <img src={theme.logo_url} alt="" className="w-7 h-7 rounded object-cover" />
          ) : (
            <div className="w-7 h-7 rounded bg-white/15 flex items-center justify-center">
              <HardHat className="w-4 h-4 brand-banner-text" strokeWidth={2.4} />
            </div>
          )}
          <div className="font-display font-extrabold text-xl tracking-widest brand-banner-text uppercase" data-testid="top-brand-desktop">
            Quick Quotes
          </div>
          {theme.company_name && (
            <div className="hidden md:block brand-banner-text opacity-70 text-xs font-semibold uppercase tracking-wider" data-testid="top-company">
              · {theme.company_name}
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <button
              data-testid="top-logout-btn"
              onClick={async () => { await logout(); window.location.href = "/"; }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/15 hover:bg-white/25 brand-banner-text font-bold text-sm tracking-wide border border-white/20 transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.4} /> Log out
            </button>
          </div>
        </header>
        <div className="px-5 sm:px-8 py-6 sm:py-8">
          <Outlet />
          <FeedbackButtons />
        </div>
      </main>
    </div>
  );
}
